import { useCallback, useEffect, useState } from 'react';
import { useModel } from '@umijs/max';
import { message } from 'antd';
import { CLOCK_IN_REQUIRED_EVENT } from '@/constants';
import { PERMISSIONS } from '@/constants/permissions';
import {
  acknowledgeAutoClose,
  clockIn as apiClockIn,
  clockOut as apiClockOut,
  getClockStatus,
  type AutoCloseNotice,
} from '@/services/app/tenant/csr-clock';

// How often clock status is re-polled, so an auto-close
// (CsrClockAutoCloseService runs every 10 min) surfaces without a reload.
const STATUS_POLL_INTERVAL_MS = 2 * 60 * 1000;

/**
 * Shared clock in/out state for the nav widget (CsrClockWidget) and the
 * full-page lock (ClockInGate), so a clock-out, an auto-close, or a
 * CLOCK_IN_REQUIRED response anywhere flips both at once.
 */
export default () => {
  // Explicit type: the inferred @@initialState type includes every model's
  // type (this one too), which makes TS give up with a circular `any`.
  const { initialState } = useModel('@@initialState') as { initialState?: { currentUser?: API.CurrentUser } };
  const currentUser: API.CurrentUser | undefined = initialState?.currentUser;
  const isSuperAdmin = !!currentUser?.is_super_admin;
  const isCsr = (currentUser as any)?.tenant_user_roles?.some((r: any) => r.role === 'CSR') ?? false;
  // Deliberately no super admin shortcut — mirrors access.mustClockIn.
  const mustClockIn = !isSuperAdmin && (currentUser?.permissions || []).includes(PERMISSIONS.CLOCKIN_REQUIRED);
  const enabled = currentUser?.user_type === 'tenant' && (isCsr || mustClockIn);

  const [clockedInAt, setClockedInAt] = useState<string | null>(null);
  const [clockedInToday, setClockedInToday] = useState(false);
  const [autoCloseNotice, setAutoCloseNotice] = useState<AutoCloseNotice | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dismissingNotice, setDismissingNotice] = useState(false);

  const refreshStatus = useCallback(async () => {
    try {
      const status = await getClockStatus();
      setClockedInAt(status.clocked_in ? status.clocked_in_at : null);
      setClockedInToday(status.clocked_in_today);
      setAutoCloseNotice((current) => {
        if (status.auto_close_notice) {
          return current?.session_id === status.auto_close_notice.session_id ? current : status.auto_close_notice;
        }
        return null;
      });
    } catch {
      // Best-effort — keep whatever state we had. For a mustClockIn user that
      // means the gate stays up; the backend rejects their calls anyway.
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;

    refreshStatus();
    const poll = window.setInterval(refreshStatus, STATUS_POLL_INTERVAL_MS);
    // Catches a clock-out done in another tab as soon as this one is looked at again.
    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshStatus();
    };
    const onClockInRequired = () => {
      setClockedInAt(null);
      refreshStatus();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener(CLOCK_IN_REQUIRED_EVENT, onClockInRequired);
    return () => {
      window.clearInterval(poll);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener(CLOCK_IN_REQUIRED_EVENT, onClockInRequired);
    };
  }, [enabled, refreshStatus]);

  const clockIn = useCallback(async () => {
    setBusy(true);
    try {
      const status = await apiClockIn();
      setClockedInAt(status.clocked_in ? status.clocked_in_at : null);
      setClockedInToday(status.clocked_in_today);
      return true;
    } catch {
      message.error('Failed to clock in');
      return false;
    } finally {
      setBusy(false);
    }
  }, []);

  const clockOut = useCallback(async () => {
    setBusy(true);
    try {
      const status = await apiClockOut();
      setClockedInAt(status.clocked_in ? status.clocked_in_at : null);
      message.success('Clocked out');
      return true;
    } catch {
      message.error('Failed to clock out');
      return false;
    } finally {
      setBusy(false);
    }
  }, []);

  const dismissAutoClose = useCallback(async () => {
    if (!autoCloseNotice) return;
    setDismissingNotice(true);
    try {
      await acknowledgeAutoClose(autoCloseNotice.session_id);
    } catch {
      // Best-effort — worst case the notice reappears on the next poll.
    } finally {
      setDismissingNotice(false);
      setAutoCloseNotice(null);
    }
  }, [autoCloseNotice]);

  return {
    enabled,
    mustClockIn,
    /** True once the first status check finished (success or failure). */
    loaded,
    /** mustClockIn user who is not clocked in — the app must be replaced by ClockInGate. */
    locked: mustClockIn && loaded && !clockedInAt,
    clockedInAt,
    clockedInToday,
    autoCloseNotice,
    busy,
    dismissingNotice,
    clockIn,
    clockOut,
    dismissAutoClose,
  };
};
